import { MockDatabase, MockRecord, MockTable, TableQuery } from "../MockDatabase";
import { v4 as uuidv4 } from 'uuid';

export type OrganizationListAPI = {
    organization_guid: string;
    display_name: string;
    machine_name: string;
    is_active: boolean;
    default_bundle_guid: string;
    okta_group_id: string | null;
    okta_application_id?: string | null;
    metadata_schema_type: string;
    created_by: string | null;
    updated_by: string | null;
    created_date: string;
    updated_date: string;
}

export type OrganizationAPI = {
    client_id: string | null;
    client_secret: string | null;
} & OrganizationListAPI;

export class MockOrganizations extends MockTable {
    constructor(db: MockDatabase) {
        super(db);
        this.name = 'organizations';
        this.fields = {
            id: this.PrimaryKey({
                fieldName: 'id'
            }),
            name: this.Field({
                type: 'string',
                fieldName: 'name',
                nullable: false,
                index: true
            }),
            organization_id: this.PublicKey({
                fieldName: 'organization_id'
            }),
            is_active: this.Field({
                type: 'boolean',
                fieldName: 'is_active',
                nullable: false
            }),
            created_date: this.Field({
                type: 'datetime',
                fieldName: 'created_date',
                nullable: false,
                default: {
                    method: 'NOW'
                }
            }),
            updated_date: this.Field({
                type: 'datetime',
                fieldName: 'updated_date',
                nullable: false,
                default: {
                    method: 'NOW',
                    onUpdate: true
                }
            }),
            primary_organization_email_address: this.Field({
                type: 'string',
                fieldName: 'primary_organization_email_address',
                nullable: true
            }),
            created_by: this.Field({
                type: 'string',
                fieldName: 'created_by',
                nullable: true,
                default: {
                    value: 'user' // Generating a default to mimic server behavior of Okta data from reading JWT
                }
            }),
            updated_by: this.Field({
                type: 'string',
                fieldName: 'updated_by',
                nullable: true,
                default: {
                    value: 'user' // Generating a default to mimic server behavior of Okta data from reading JWT
                }
            }),
            okta_group_id: this.Field({
                type: 'UUID', // not truly a UUID in deployment
                fieldName: 'okta_group_id',
                nullable: true,
                default: {
                    method: 'UUID' // Generating a default to mimic the server behavior of creating a group in Okta
                }
            }),
            okta_application_id: this.Field({
                type: 'UUID', // not truly a UUID in deployment
                fieldName: 'okta_application_id',
                nullable: true,
                default: {
                    method: 'UUID' // Generating a default to mimic the server behavior of creating an application in Okta
                }
            }),
            metadata_schema_type: this.Field({
                type: 'string',
                fieldName: 'metadata_schema_type',
                nullable: true
            }),
            machine_name: this.Field({
                type: 'string',
                fieldName: 'machine_name',
                nullable: true,
                index: true
            }),
            default_bundle_id: this.ForeignKey({
                fieldName: 'default_bundle_id',
                nullable: true,
                foreignTableName: 'bundles',
                foreignTableFieldName: 'id',
            }),
        } as const;
    }
    
    private _transformPayload(db: MockDatabase, values: Record<string, any>): Record<string, any> {
        let {
            organization_guid, organizationGuid, organization_id,
            ...data
        } = values;
        if ((organization_guid || organizationGuid) && !organization_id) {
            organization_id = organization_guid || organizationGuid; // organization_id is the public key
        }
        return {
            ...data,
            ...(organization_id ? {organization_id} : {}),
        }
    }
    
    protected beforeQuery: (db: MockDatabase, table: MockTable, query: TableQuery) => TableQuery = (db, table, query) => {
        const transFormFields: Record<string, any> = {};
        const remQuery: TableQuery = [];
        query.forEach(entry => {
            if (entry.fieldName === 'organization_guid' || entry.fieldName === 'organizationGuid') {
                transFormFields['organization_guid'] = entry.value;
            } else {
                if (entry.fieldName === 'display_name' || entry.fieldName === 'displayName') {
                    entry.fieldName = 'name';
                }
                remQuery.push(entry);
            }
        });
        const fixed = this._transformPayload(db, transFormFields);
        Object.entries(fixed).forEach(([fieldName, value]) => remQuery.unshift({
            fieldName,
            value
        }));
        return remQuery;
    }

    protected beforeUpdate: (db: MockDatabase, table: MockTable, records: MockRecord[], data: Record<string, any>) => Record<string, any> = (db, table, records, data) => {
        data = this._transformPayload(db, data);
        if (data['display_name']) {
            data['name'] = data['display_name'];
            delete data['display_name'];
        }
        if (data['machine_name']) {
            const machineNameExists = this.query([]).some(record =>
                record.values['machine_name'] === data['machine_name'] && records.indexOf(record) === -1
            );
            if (machineNameExists) {
                throw new Error(`An Organization with the machine_name "${data['machine_name']}" already exists`);
            }
        }
        return data;
    }

    private _baseSerializer = (record: MockRecord) => {
        let bundle_guid: string | null = null;
        if (record.values['default_bundle_guid'] !== null) {
            const bundleRecs = record.getRelated('bundles');
            if (bundleRecs.length > 0) {
                bundle_guid = bundleRecs[0].values['bundle_guid'];
            }
        }
        return {
            organization_guid: record.values['organization_id'],
            display_name: record.values['name'],
            machine_name: record.values['machine_name'],
            is_active: record.values['is_active'],
            default_bundle_guid: bundle_guid,
            okta_group_id: record.values['okta_group_id'],
            metadata_schema_type: record.values['metadata_schema_type'],
            created_by: record.values['created_by'],
            updated_by: record.values['updated_by'],
            created_date: record.values['created_date'],
            updated_date: record.values['updated_date'],
        }
    }

    serialize = (record: MockRecord) => ({
        ...this._baseSerializer(record),
        client_id: uuidv4(),
        client_secret: uuidv4(),
    }) as OrganizationAPI;

    serializeList = (records: MockRecord[]) => records.map(record => (
        this._baseSerializer(record)
    )) as OrganizationListAPI[];

    protected beforeCreate: (db: MockDatabase, table: MockTable, values: Record<string, any>) => Record<string, any> = (db: MockDatabase, table: MockTable, values: Record<string, any>) => {
        // Handle changing display_name to name
        if (values['display_name']) {
            values['name'] = values['display_name'];
            delete values['display_name'];
        }
        if (!values['name']) {
            throw new Error(`Missing required field "name" when creating Organization`);
        }
        // Handle distinct name
        const displayNameExists = (this.getIndex('name') || {})[values['name']];
        if (displayNameExists) {
            throw new Error(`An Organization with the name "${values['name']}" already exists`);
        }
        const machineNameExists = (this.getIndex('machine_name') || {})[values['machine_name']];
        if (machineNameExists) {
            throw new Error(`An Organization with the machine_name "${values['machine_name']}" already exists`);
        }
        return values;
    }

    protected formatQuery = (query: TableQuery) => query.map(entry => {
        if (entry.fieldName === 'organization_guid') {
            entry.fieldName = 'organization_id';
        }
        return entry;
    });
}